import { SubFormProps } from '@forms/Components/types';
import { useFormikValues } from '@hooks/useFormikValues';
import { Field, useFormikContext } from 'formik';
import { Alert } from '@mui/material';
import { ContainerFormik, containerFormValidationSchema, convertKilogramsToPounds, convertPoundsToKilograms } from './types';
import { FormikDateTimePicker } from '@forms/Components/DateTimePicker';
import withSubForm from '@forms/Components/withSubForm';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { LegType, legTypeHasChassis, legTypeHasContainer, noChassisReason, noContainerReason } from '@api/Trinium/types';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { FormikTextInput } from '@forms/Components/TextInput';
import { FormikNumberInput } from '@forms/Components/NumberInput';
import { FormikCheckboxInput } from '@forms/Components/CheckInput';
import { FormikAutocomplete } from '@forms/Components/AutocompleteInput';
import { useMemo } from 'react';
import { Chassis, useFetchChassisQuery } from '@api/Trinium/Endpoints/Chassis/slice';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { createSelector } from '@reduxjs/toolkit';
import { FormikDatePicker } from '../Components/DatePicker';

import styles from './Form.module.css';

const ContainerForm = (props: SubFormProps) => {
  useFormikValues(props);
  const formik = useFormikContext<ContainerFormik>();
  const { number: terminalNumber } = useCurrentTerminal();

  const legType = getEnumValueByKey(LegType, formik.values.legType);
  const hasContainer = legTypeHasContainer(formik.values.legType);
  const hasChassis = legTypeHasChassis(formik.values.legType);

  const selectFilteredChassis = useMemo(() => {
    const noChassis: Chassis[] = [];

    return createSelector(
      (chassis: Chassis[] | null | undefined) => chassis,
      (_: Chassis[] | null | undefined, searchInput: string | null | undefined) => searchInput,
      (chassis, searchInput) => {
        if (!chassis) {
          return noChassis;
        }

        if (!searchInput) {
          return chassis;
        }

        return chassis.filter((chs) => chs.id.toLowerCase().includes(searchInput.toLowerCase())).slice(0, 50);
      }
    );
  }, []);

  const { filteredChassis } = useFetchChassisQuery(
    { terminalNumber, status: 'Available' },
    {
      selectFromResult: ({ data, isFetching }) => ({
        filteredChassis: selectFilteredChassis(data, formik.values.chassisNumber),
        isFetching
      })
    }
  );

  /** Centralized change method to convert pounds and kilograms back and forth */
  const weightChangeEvent = (value: number | null, unit: 'lb' | 'kg') => {
    if (value === null || value === 0) {
      void formik.setFieldValue('weightInKg', null, true);
      void formik.setFieldValue('weightInPounds', null, true);
    } else if (unit === 'lb') {
      /** Direction is determined by the originating field's unit. We intentionally do not check
       * document.activeElement here: the conversion runs on a debounce that can fire after the
       * user has tabbed away, so a focus check would silently skip the conversion on fast entry. */
      void formik.setFieldValue('weightInPounds', value, true);
      const kgWeight = convertPoundsToKilograms(value);
      void formik.setFieldValue('weightInKg', kgWeight, true);
    } else {
      void formik.setFieldValue('weightInKg', value, true);
      const poundsWeight = convertKilogramsToPounds(value);
      void formik.setFieldValue('weightInPounds', poundsWeight, true);
    }
  };

  const availabilityDatesOnly = props.fieldProps.availabilityDatesOnly;

  const bulkDisabledFieldReasons = availabilityDatesOnly
    ? [
        props.fieldProps.bulkFullAvailableDisabled && { label: 'Full Container Available', reason: 'not every selected order is a Full Container Leg' },
        props.fieldProps.bulkEmptyAvailableDisabled && { label: 'Empty Container Available', reason: 'not every selected order is an Empty Container Leg' },
        props.fieldProps.bulkChassisAvailableDisabled && { label: 'Chassis Available', reason: 'not every selected order supports a chassis' }
      ].filter((entry): entry is { label: string; reason: string } => Boolean(entry))
    : [];

  // A single disabled field gets its specific reason; multiple disabled fields fall back to the general explanation
  const bulkDisabledMessage =
    bulkDisabledFieldReasons.length === 1
      ? `${bulkDisabledFieldReasons[0].label} can't be updated because ${bulkDisabledFieldReasons[0].reason}.`
      : bulkDisabledFieldReasons.length > 1
        ? `${bulkDisabledFieldReasons.map((entry) => entry.label).join(' and ')} can't be updated because the selected orders don't all share the same leg type.`
        : undefined;

  return (
    <div className={styles.form}>
      <div className={styles['container-info']}>
        {!availabilityDatesOnly && (
        <div className={styles['flex-row']}>
          <div className={styles['flex-item']}>
            <div className={styles['flex-row']}>
              <Field
                name='containerNumber'
                label='Container'
                component={FormikTextInput}
                className={styles.container}
                disabled={!hasContainer}
                disabledReason={!hasContainer ? noContainerReason : undefined}
              />

              <div className={`${styles['flex-item']} ${styles['size-flex-item']}`}>
                <span className={styles['label']}>Size</span>
                <span className={styles['size']}>{formik.values.size ?? '-'}</span>
              </div>

              <div className={styles['flex-item']}>
                <span className={styles['label']}>Type</span>
                <span className={styles['type']}>{formik.values.type ?? '-'}</span>
              </div>
            </div>
            <div className={styles['flex-row']}>
              <Field
                name={'chassisNumber'}
                label={'Chassis'}
                component={FormikAutocomplete}
                freeSolo
                className={styles.chassis}
                autoCompleteItems={filteredChassis.map((chassis) => chassis.id)}
                onChangeExtension={(value: string) => {
                  const isOwned = filteredChassis.some((chassis) => chassis.id === value);
                  void formik.setFieldValue('isOwned', isOwned);
                }}
                disabled={!hasChassis}
                disabledReason={!hasChassis ? noChassisReason : undefined}
              />

              <Field
                name='isOwned'
                component={FormikCheckboxInput}
                label='Is Owned'
                disabled
                disabledReason='Field is not editable'
                disabledTooltipClass={styles['chk-center']}
              />

              <Field
                name='streetTurn'
                component={FormikCheckboxInput}
                label='Street Turn'
                className={styles['chk-center']}
                onChangeExtension={(value: boolean) => {
                  if (value) {
                    /** Because this is a date, if you uncheck and then recheck the checkbox it would appear dirty
                     *  To resolve this, if we had a date coming into the form than reuse that else use now.
                     */
                    void formik.setFieldValue('streetTurn', formik.initialValues.streetTurn ? formik.initialValues.streetTurn : dayjsNow());
                  } else {
                    /** On uncheck, set to null */
                    void formik.setFieldValue('streetTurn', null);
                  }
                }}
              />
            </div>
          </div>

          <div className={`${styles['flex-item']} ${styles['steamship-info']}`}>
            <div className={styles['flex-row']}>
              <div className={styles['flex-item']}>
                <span className={styles['label']}>Vessel</span>
                <span
                  className={styles['vessel']}
                  title={formik.values.vessel}>
                  {formik.values.vessel ?? '-'}
                </span>
              </div>
            </div>

            <div className={styles['flex-row']}>
              <div className={styles['flex-item']}>
                <span className={styles['label']}>Voyage</span>
                <span className={styles.voyage}>{formik.values.voyage ?? '-'}</span>
              </div>

              {formik.values.catTransType === 'Export' && (
                <div className={styles['flex-item']}>
                  <span
                    className={styles['label']}
                    title='First Return Date'>
                    First Return Date
                  </span>
                  <span className={styles.voyage}>{formik.values.firstReceiveDate?.format('MM/DD/YY') ?? '-'}</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles['flex-item']}>
            <div className={styles['flex-row']}>
              {props.fieldProps.extended && (
                <>
                  <Field
                    name='pieces'
                    component={FormikNumberInput}
                    max={9999999}
                    label='Pieces'
                    className={styles.pieces}
                    disabled={legType === LegType.ECL}
                    disabledReason={legType === LegType.ECL ? 'Field is not available for an ECL Leg' : undefined}
                  />

                  <Field
                    name='weightInPounds'
                    component={FormikNumberInput}
                    max={9999999}
                    label='Weight (lb)'
                    className={styles.pounds}
                    onChangeExtension={(value: number) => {
                      weightChangeEvent(value, 'lb');
                    }}
                    disabled={legType === LegType.ECL}
                    disabledReason={legType === LegType.ECL ? 'Field is not available for an ECL Leg' : undefined}
                  />

                  <Field
                    name='weightInKg'
                    component={FormikNumberInput}
                    max={4535923}
                    label='Weight (kg)'
                    className={styles.kilograms}
                    onChangeExtension={(value: number) => {
                      weightChangeEvent(value, 'kg');
                    }}
                    disabled={legType === LegType.ECL}
                    disabledReason={legType === LegType.ECL ? 'Field is not available for an ECL Leg' : undefined}
                  />
                </>
              )}
            </div>

            <div className={styles['flex-row']}>
              {props.fieldProps.extended && (
                <Field
                  name='sealNo'
                  component={FormikTextInput}
                  label='Seal'
                  className={styles.seal}
                />
              )}
            </div>
          </div>
        </div>
        )}

        {bulkDisabledMessage && (
          <Alert
            severity='info'
            className={styles['bulk-disabled-note']}>
            {bulkDisabledMessage}
          </Alert>
        )}

        <div className={styles['flex-row']}>
          <Field
            name='fullAvailableDate'
            component={FormikDateTimePicker}
            label='Full Container Available'
            className={`${styles['date-input']} ${styles['full-available']}`}
            disabled={availabilityDatesOnly ? props.fieldProps.bulkFullAvailableDisabled : legType !== LegType.FCL}
            disabledReason={
              availabilityDatesOnly
                ? props.fieldProps.bulkFullAvailableDisabled
                  ? 'Not every selected order is a Full Container Leg'
                  : undefined
                : legType !== LegType.FCL
                  ? 'Not an FCL Leg'
                  : undefined
            }
            nowBtn
          />

          <Field
            name='emptyAvailableDate'
            component={FormikDateTimePicker}
            label='Empty Container Available'
            className={`${styles['date-input']} ${styles['empty-available']}`}
            disabled={availabilityDatesOnly ? props.fieldProps.bulkEmptyAvailableDisabled : legType !== LegType.ECL}
            disabledReason={
              availabilityDatesOnly
                ? props.fieldProps.bulkEmptyAvailableDisabled
                  ? 'Not every selected order is an Empty Container Leg'
                  : undefined
                : legType !== LegType.ECL
                  ? 'Not an ECL Leg'
                  : undefined
            }
            nowBtn
          />

          <Field
            name='chassisAvailableDate'
            component={FormikDateTimePicker}
            label='Chassis Available'
            className={styles['date-input']}
            disabled={availabilityDatesOnly && props.fieldProps.bulkChassisAvailableDisabled}
            disabledReason={
              availabilityDatesOnly && props.fieldProps.bulkChassisAvailableDisabled
                ? 'Not every selected order supports a chassis'
                : undefined
            }
            nowBtn
          />

          {!availabilityDatesOnly && (formik.values.catTransType === 'Import' || formik.values.catTransType === 'Local') && (
            <Field
              name='lastFreeDate'
              component={FormikDatePicker}
              label='LFD (Last Free Day)'
              className={styles['date-input']}
              disabled={legType !== LegType.FCL}
            />
          )}
          {!availabilityDatesOnly && formik.values.catTransType === 'Export' && (
            <Field
              name='cutoffDate'
              component={FormikDateTimePicker}
              label='Cutoff'
              className={`${styles['date-input']} ${styles['empty-available']}`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const ContainerSubform = withSubForm(ContainerForm, containerFormValidationSchema);
